import { F } from '@/utils/intl';
import React from 'react';
import { useIntl } from 'umi';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import { Alert, Button, Col, Row, Space, Spin, Typography } from 'antd';
import InfoCard from '@/components/ProjectDetails/Cards/InfoCard';
import LeadLocale from '@/components/Lead/LeadLocale';
import { BankOutlined, UserOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import contactStyles from '@/components/ProjectDetails/Contacts/index.module.less';
import LeadSalutation from '@/components/Lead/LeadSalutation';
import { FormInstance } from 'antd/lib/form';
import styles from './index.less';
import ExtraFields from '@/components/LeadSource/ManualLead/ExtraFields';
import CustomerTypeToggle from '@/components/Contacts/CustomerTypeToggle';
import RoleInput from '@/components/Contacts/RoleInput';
import { useContactRoleSuggestions } from '@/components/Contacts/useContactRoleSuggestions';
import { CUSTOMER_TYPES, CustomerType } from '@/typings/models/Contact';

interface CustomerFormProps {
  form: FormInstance;
  isCustomerFormDisabled: boolean;
  isSearchingCustomers: boolean;
  customerRequiredFields: string[];
  onEmailBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onPhoneBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onCustomerExtraFieldValuesChange: (values: any) => void;
  customerType: CustomerType;
  onCustomerTypeChange: (type: CustomerType) => void;
  onCompanyBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** An existing company with the typed name. */
  matchingCompany?: { company_name?: string | null } | null;
  onUseMatchingCompany: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  form,
  isCustomerFormDisabled,
  isSearchingCustomers,
  customerRequiredFields,
  onEmailBlur,
  onPhoneBlur,
  onCustomerExtraFieldValuesChange,
  customerType,
  onCustomerTypeChange,
  onCompanyBlur,
  matchingCompany,
  onUseMatchingCompany,
}) => {
  const intl = useIntl();
  const roleSuggestions = useContactRoleSuggestions();
  const isCompany = customerType === CUSTOMER_TYPES.ORGANIZATION;

  // A company needs only its name; the partner's required person fields don't apply.
  const isFieldRequired = (fieldName: string) => {
    return !isCustomerFormDisabled && !isCompany && customerRequiredFields.includes(fieldName);
  };

  return (
    <div>
      <InfoCard
        style={{
          display: !isCustomerFormDisabled ? 'block' : 'none',
        }}
        title={
          <Space>
            {isCompany ? <BankOutlined /> : <UserOutlined />}
            <F
              id={'pages.lead.customer_information'}
              defaultMessage={'Customer Information'}
            />
          </Space>
        }
        variant="blue"
        className={styles.infoCardSection}
        extra={
          <CustomerTypeToggle
            value={customerType}
            onChange={onCustomerTypeChange}
            disabled={isCustomerFormDisabled}
          />
        }
      >
        <ProForm
          form={form}
          autoComplete="off"
          size="small"
          submitter={false}
          variant={'underlined'}
        >
          {/* A div, not a fragment: ProForm hands its first child `autoFocus`. */}
          {isCompany && (
            <div>
              <ProFormText
                label={intl.formatMessage({ id: 'component.contacts.company', defaultMessage: 'Company' })}
                name="company_name"
                rules={[{ required: !isCustomerFormDisabled }, { max: 255 }]}
                fieldProps={{
                  autoComplete: 'nope',
                  autoFocus: true,
                  onBlur: onCompanyBlur,
                  'data-testid': 'customer-company-name',
                }}
              />
              {matchingCompany && (
                <Alert
                  type="warning"
                  className={styles.companyExistsAlert}
                  title={
                    <span>
                      <F
                        id="component.contacts.companyExists"
                        defaultMessage="“{company}” already exists."
                        values={{ company: matchingCompany.company_name }}
                      />{' '}
                      <Button type="link" size="small" onClick={onUseMatchingCompany}>
                        <F
                          id="component.contacts.useExistingCompany"
                          defaultMessage="Create the project for it"
                        />
                      </Button>
                    </span>
                  }
                />
              )}
            </div>
          )}
          {/* As on the project card: the box holds the contact; fields outside it belong to the customer. */}
          <div className={classNames({ [contactStyles.contactBox]: isCompany })}>
          {isCompany && (
            <Typography.Text className={styles.contactPersonTitle}>
              <F id="component.contacts.contactPerson" defaultMessage="Contact person" />
            </Typography.Text>
          )}
          <Row gutter={16}>
            <Col span={4}>
              <ProForm.Item
                label={
                  <F id={'customer.salutation'} defaultMessage={'Salutation'} />
                }
                rules={[
                  {
                    required: isFieldRequired('gender'),
                  },
                ]}
                name="gender"
              >
                <LeadSalutation
                  lead={null}
                  selectProps={{
                    variant: 'underlined',
                    size: 'small',
                    allowClear: true,
                    disabled: isCustomerFormDisabled,
                  }}
                />
              </ProForm.Item>
            </Col>
            <Col span={10}>
              <ProFormText hidden name={'customerId'} />
              <ProFormText
                label={intl.formatMessage({
                  id: 'pages.customers.firstName',
                  defaultMessage: 'First Name',
                })}
                name="firstname"
                disabled={isCustomerFormDisabled}
                rules={[
                  {
                    required: !isCustomerFormDisabled && !isCompany,
                  },
                ]}
                fieldProps={{
                  autoComplete: 'nope',
                  'data-testid': 'customer-firstname',
                }}
              />
            </Col>
            <Col span={10}>
              <ProFormText
                label={intl.formatMessage({
                  id: 'pages.customers.lastName',
                  defaultMessage: 'Last Name',
                })}
                name={'lastname'}
                rules={[
                  {
                    required: !isCustomerFormDisabled && !isCompany,
                  },
                ]}
                fieldProps={{
                  autoComplete: 'nope',
                  'data-testid': 'customer-lastname',
                }}
              />
            </Col>
          </Row>

          <Spin spinning={isSearchingCustomers}>
            <Row gutter={16}>
              <Col span={10}>
                <ProFormText
                  label={intl.formatMessage({
                    id: 'common.attribute.email',
                    defaultMessage: 'Email',
                  })}
                  name="email"
                  rules={[
                    {
                      required: isFieldRequired('email'),
                    },
                    {
                      type: 'email',
                    },
                  ]}
                  fieldProps={{
                    autoComplete: 'nope',
                    onBlur: onEmailBlur,
                    'data-testid': 'customer-email',
                  }}
                />
              </Col>
              <Col span={10}>
                <ProFormText
                  label={intl.formatMessage({
                    id: 'common.attribute.phone',
                    defaultMessage: 'Phone',
                  })}
                  name="phone"
                  rules={[
                    {
                      required: isFieldRequired('phone'),
                    },
                  ]}
                  fieldProps={{
                    autoComplete: 'nope',
                    onBlur: onPhoneBlur,
                  }}
                />
              </Col>
              <Col span={4}>
                <ProForm.Item
                  label={
                    <F
                      id={'pages.leads.filters.locale.placeholder'}
                      defaultMessage={'Language'}
                    />
                  }
                  rules={[
                    {
                      required: isFieldRequired('locale'),
                    },
                  ]}
                  name="locale"
                >
                  <LeadLocale
                    selectProps={{
                      variant: 'underlined',
                      size: 'small',
                      disabled: isCustomerFormDisabled,
                    }}
                  />
                </ProForm.Item>
              </Col>
            </Row>
          </Spin>

          {isCompany && (
            <Row gutter={16}>
              <Col xs={24} sm={10}>
                <ProForm.Item label={<F id="entity.role.singular" defaultMessage="Role" />} name="role">
                  <RoleInput suggestions={roleSuggestions} />
                </ProForm.Item>
              </Col>
            </Row>
          )}
          </div>

          {/* Customer Extra Fields */}
          <ExtraFields
            relatedTo="customer"
            onValuesChange={onCustomerExtraFieldValuesChange}
          />
        </ProForm>
      </InfoCard>
    </div>
  );
};

export default CustomerForm;
